import { Select } from 'antd';
import classNames from 'classnames';
import React, { forwardRef, useEffect, useRef } from 'react';
import { arrowDownGreyIcon, checkMarkIcon, clearIcon } from './assets';
import ErrorMessage from '../errorMessage';
import InfoMessage from '../infoMessage';
import InputTitle from '../inputTitle';
import type { ICustomSelectProps } from './types';
import { useCustomSelect } from './useCustomSelect';
import styles from './style.module.scss';

export type { ICustomSelectProps, ValueSelect, optionSelect } from './types';

const EMPTY_OPTIONS: ICustomSelectProps['options'] = [];

/**
 * Project-wide select control wrapping antd Select with a title, error and info message, custom clear, check and arrow icons and dropdown variants for device ID and RMA reason. Defaults size, clearing, search and layout options, forwards ref and change handling to useCustomSelect, applies a case-insensitive label filter unless filterOption is provided, and keeps the dropdown open state in sync with props.open.
 *
 * @param props - Options, value, display settings and the select callbacks.
 * @param ref - Forwarded ref exposing the underlying select input.
 * @returns The select field markup with its messages.
 */
const CustomSelect = forwardRef((props: ICustomSelectProps, ref): React.JSX.Element => {
  const {
    allowClear = true,
    bordered = true,
    disabled = false,
    loading = false,
    placement = 'bottomLeft',
    size = 'large',
    showSearch = true,
    options = EMPTY_OPTIONS,
    popupMatchSelectWidth = true,
    labelInValue = false,
    isSelectOnlyClearIcon = false,
  } = props;
  const { dropdownOpen, seletInputRef, onChange, handleDropdownVisibleChange } = useCustomSelect(
    {
      name: props.name,
      onChange: props.onChange,
      onDropdownVisibleChange: props.onDropdownVisibleChange,
    },
    ref,
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const resolvedAriaLabel =
    props['aria-label'] ?? (typeof props.title === 'string' ? props.title : undefined);
  const isDropdownOpen = props.open || dropdownOpen;

  // rc-select hard-codes role="listbox" on its popup with no prop to give it a name,
  // and per the ARIA spec that role takes its name from an author label only, never
  // from its (possibly empty) option text — so it must be labelled imperatively once
  // the popup mounts. A MutationObserver is needed (not a plain effect) because
  // rc-trigger mounts the popup a tick after `open` turns true.
  useEffect(() => {
    if (!isDropdownOpen || !resolvedAriaLabel || !wrapperRef.current) {
      return undefined;
    }
    /**
     * Gives the select's dropdown listbox the same accessible name as the select itself.
     * Writes the resolved aria-label onto the given popup node.
     *
     * @param node - The listbox element rendered by the select popup.
     * @returns Nothing.
     */
    const label = (node: Element) => node.setAttribute('aria-label', resolvedAriaLabel);
    const existing = wrapperRef.current.querySelector('[role="listbox"]');
    if (existing) {
      label(existing);
      return undefined;
    }
    const observer = new MutationObserver(() => {
      const listbox = wrapperRef.current?.querySelector('[role="listbox"]');
      if (listbox) {
        label(listbox);
        observer.disconnect();
      }
    });
    observer.observe(wrapperRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isDropdownOpen, resolvedAriaLabel]);

  return (
    <div ref={wrapperRef} className={props.className}>
      <InputTitle title={props.title} isRequired={props.isRequired} />
      <Select
        ref={seletInputRef}
        id={props.id}
        aria-label={resolvedAriaLabel}
        aria-labelledby={props['aria-labelledby']}
        className={classNames(
          styles.customSelect,
          'custom-select-test',
          props.formatDropdownType === 'deviceId' && '--dropdown-deviceId',
          props.formatDropdownType === 'reasonRMA' && '--dropdown-reason-rma',
          props.innerClassName,
          isSelectOnlyClearIcon && 'custom-select-only-clear-icon',
        )}
        open={props.open || dropdownOpen}
        allowClear={allowClear}
        bordered={bordered}
        defaultValue={props.defaultValue}
        clearIcon={<img src={clearIcon} alt="Clear icon" />}
        menuItemSelectedIcon={<img width={16} height={16} src={checkMarkIcon} alt="" />}
        popupClassName={props.popupClassName}
        disabled={disabled}
        dropdownRender={props.dropdownRender}
        filterOption={
          (props.filterOption !== undefined
            ? props.filterOption
            : (input: any, option: any) =>
                option?.label
                  ? option.label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
                  : true) as any
        }
        getPopupContainer={
          props.getPopupContainer
            ? props.getPopupContainer
            : (triggerNode) => triggerNode.parentElement
        }
        loading={loading}
        status={props.isError ? 'error' : ''}
        mode={props.mode}
        notFoundContent={props.notFoundContent}
        options={options as any}
        placeholder={props.placeholder}
        placement={placement}
        removeIcon={props.removeIcon}
        showArrow={props.showArrow}
        showSearch={showSearch}
        size={size}
        popupMatchSelectWidth={popupMatchSelectWidth}
        suffixIcon={
          loading
            ? undefined
            : props.suffixIcon || <img src={arrowDownGreyIcon} alt="" width={16} height={16} />
        }
        value={props.value}
        onChange={onChange}
        style={props.style}
        optionLabelProp="title" // customize the display content of the label.
        dropdownStyle={props.dropdownStyle}
        labelInValue={labelInValue}
        onSearch={props.onSearch}
        searchValue={props.searchValue}
        onInputKeyDown={props.onInputKeyDown}
        dropdownAlign={props.dropdownAlign}
        onClick={props.onClick}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        onPopupScroll={props.onPopupScroll}
      />
      {props.errorMessage && <ErrorMessage errorMessage={props.errorMessage} hasIcon />}
      {props.infoMessage && <InfoMessage infoMessage={props.infoMessage} hasIcon />}
    </div>
  );
});

export default CustomSelect;
